import { Fragment, useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Eye, EyeSlash, LockSimple } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { dmsApi } from './dmsApi';

/** サイドバーの表示 — ロール単位で画面を隠す（隠した画面は裏のAPIも閉じる）。 */
export default function NavVisibilityCard() {
  const [data, setData] = useState(null);
  const [vis, setVis] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await dmsApi.navVisibility();
      setData(d);
      setVis(d.visibility || {});
    } catch (e) { toast.error(e?.response?.data?.detail || '表示設定の取得に失敗しました'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await dmsApi.saveNavVisibility(vis);
      setVis(r.visibility);
      toast.success('サイドバーの表示を保存しました');
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const sections = [...new Set(data.items.map((i) => i.section))];

  return (
    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm p-4"
      data-testid="dms-admin-nav-visibility">
      <p className="text-sm font-bold text-zinc-800 flex items-center gap-1.5 mb-1">
        <Eye size={16} weight="duotone" className="text-indigo-600" /> サイドバーの表示
      </p>
      <p className="text-[11px] text-zinc-400 mb-3">
        ロールごとに、ドキュメントのメニュー項目を見せるかどうかを決めます。
        隠した画面はメニューから消え、その画面のAPIも 403 になります（共有URLを踏んでも開けません）。
        管理者は常にすべて見えます。「すべての書類」はモジュールの入口なので消せません。
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[11px] text-zinc-500">
              <th className="py-1.5 pr-3 font-semibold">画面</th>
              {data.roles.map((r) => (
                <th key={r.value} className="py-1.5 px-2 font-semibold w-28 text-center">{r.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((sec) => (
              <Fragment key={sec}>
                <tr>
                  <td colSpan={data.roles.length + 1}
                    className="pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{sec}</td>
                </tr>
                {data.items.filter((i) => i.section === sec).map((it) => (
                  <tr key={it.key} className="border-t border-zinc-100"
                    data-testid={`dms-nav-row-${it.key}`}>
                    <td className="py-2 pr-3">
                      <span className="flex items-center gap-1.5 text-zinc-800">
                        {it.label}
                        {!it.hideable && (
                          <span className="inline-flex items-center gap-0.5 rounded bg-zinc-100 px-1.5 py-0.5 text-[9px] text-zinc-500">
                            <LockSimple size={9} /> 常に表示
                          </span>
                        )}
                        {it.hideable && !it.blocks_api && (
                          <span className="rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[9px] text-amber-700">
                            メニューのみ
                          </span>
                        )}
                      </span>
                    </td>
                    {data.roles.map((r) => {
                      const on = vis[it.key]?.[r.value] !== false;
                      return (
                        <td key={r.value} className="py-2 px-2 text-center">
                          <span className="inline-flex items-center gap-1.5">
                            <Switch checked={on} disabled={!it.hideable}
                              onCheckedChange={(v) => setVis((s) => ({
                                ...s, [it.key]: { ...(s[it.key] || {}), [r.value]: v },
                              }))}
                              data-testid={`dms-nav-switch-${it.key}-${r.value}`} />
                            {it.hideable && (on
                              ? <Eye size={12} className="text-teal-600" />
                              : <EyeSlash size={12} className="text-zinc-400" />)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy}
          className="h-9 text-xs bg-teal-700 hover:bg-teal-800" data-testid="dms-nav-visibility-save">
          {busy ? '保存中…' : '保存'}
        </Button>
        <Button size="sm" variant="outline" onClick={load} disabled={busy}
          className="h-9 text-xs" data-testid="dms-nav-visibility-reload">元に戻す</Button>
      </div>
    </div>
  );
}
